import React, { useState, useEffect, useLayoutEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
	CogIcon,
	ArrowRightStartOnRectangleIcon,
	BookOpenIcon,
	ChartBarIcon,
	Squares2X2Icon,
	ChevronLeftIcon,
	Bars3Icon,
} from '@heroicons/react/24/outline'
import { fetchWithAuth } from '../../utils/apiClient'
import useGlobalTheme from '../../hooks/useGlobalTheme'
import Logo from '../../media/logo.svg'

const COLLAPSED_WIDTH = '4.25rem'
const EXPANDED_WIDTH = '14rem'
let lastPointerPosition = null

const getCollapsedWidthPx = () => {
	if (typeof window === 'undefined') return 68
	const rootFontSize = Number.parseFloat(
		window.getComputedStyle(document.documentElement).fontSize
	)
	return 4.25 * (Number.isFinite(rootFontSize) ? rootFontSize : 16)
}

const isPointerInsideCollapsedRail = () => {
	if (typeof window === 'undefined' || !lastPointerPosition) return false
	return (
		lastPointerPosition.x >= 0 &&
		lastPointerPosition.x <= getCollapsedWidthPx() &&
		lastPointerPosition.y >= 0 &&
		lastPointerPosition.y <= window.innerHeight
	)
}

const Tooltip = ({ children, isLight }) => (
	<span
		className={`pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium opacity-0 shadow-lg ring-1 transition-opacity duration-150 group-hover/menu-tooltip:opacity-100 ${
			isLight
				? 'bg-gray-900 text-white ring-black/10'
				: 'bg-white text-gray-900 ring-white/10'
		}`}
	>
		{children}
	</span>
)

const baseNavItems = [
	{ name: 'Dashboard', icon: ChartBarIcon, path: '/dashboard' },
	{ name: 'MultiBoard', icon: Squares2X2Icon, path: '/multiboard' },
	{ name: 'Tutorial', icon: BookOpenIcon, path: '/tutorial' },
	{ name: 'Settings', icon: CogIcon, path: '/settings' },
]

function SidePanel({ isOpen, onOpen = () => {}, onClose }) {
	const location = useLocation()
	const theme = useGlobalTheme()
	const isLight = theme === 'light'

	const navigate = useNavigate()
	const [isLoggingOut, setIsLoggingOut] = useState(false)
	const [isRailHovered, setIsRailHovered] = useState(() =>
		!isOpen && isPointerInsideCollapsedRail()
	)

	const navItems = baseNavItems

	const handleLogout = async () => {
		if (isLoggingOut) return
		setIsLoggingOut(true)
		try {
			await fetchWithAuth('/api/user/logout-session', { method: 'POST' })
		} catch (error) {
			console.error('Error logging out session:', error)
		} finally {
			localStorage.removeItem('jwtToken')
			localStorage.removeItem('email')
			navigate('/')
		}
	}

	useEffect(() => {
		const handlePointerMove = event => {
			lastPointerPosition = { x: event.clientX, y: event.clientY }
		}

		window.addEventListener('pointermove', handlePointerMove, { passive: true })
		return () => window.removeEventListener('pointermove', handlePointerMove)
	}, [])

	useLayoutEffect(() => {
		document.body.classList.add('has-app-side-panel')

		return () => {
			document.body.classList.remove('has-app-side-panel')
			document.documentElement.style.removeProperty('--app-side-panel-width')
		}
	}, [])

	useLayoutEffect(() => {
		document.documentElement.style.setProperty(
			'--app-side-panel-width',
			isOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH
		)
	}, [isOpen])

	useLayoutEffect(() => {
		setIsRailHovered(!isOpen && isPointerInsideCollapsedRail())
	}, [isOpen, location.pathname])

	const isActivePath = path =>
		location.pathname === path || location.pathname.startsWith(`${path}/`)

	const panelWidth = isOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH
	const panelBg = isLight
		? 'bg-white text-gray-700 border-gray-200'
		: 'bg-[#15202b] text-gray-300 border-white/[0.06]'
	const subtleText = isLight ? 'text-gray-500' : 'text-gray-500'
	const iconButton = isLight
		? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
		: 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-100'
	const activeItem = isLight
		? 'bg-gray-100 text-gray-950'
		: 'bg-white/10 text-white'
	const inactiveItem = isLight
		? 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
		: 'text-gray-400 hover:bg-white/[0.05] hover:text-gray-200'
	const showCollapsedMenuIcon = !isOpen && isRailHovered

	return (
		<aside
			className={`group fixed left-0 top-0 z-30 flex h-screen flex-col overflow-visible border-r ${panelBg}`}
			style={{ width: panelWidth }}
			aria-label="Primary navigation"
			onPointerEnter={() => {
				if (!isOpen) setIsRailHovered(true)
			}}
			onPointerLeave={() => setIsRailHovered(false)}
		>
			<div className={`flex h-16 shrink-0 items-center ${isOpen ? 'justify-between px-4' : 'justify-center px-3'}`}>
				{isOpen ? (
					<Link
						to="/dashboard"
						className="flex min-w-0 items-center gap-2"
						aria-label="LoadLine dashboard"
					>
						<img src={Logo} alt="" className="h-6 w-6 shrink-0" />
						<span className={`truncate text-base font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>
							LoadLine
						</span>
					</Link>
				) : (
					<button
						type="button"
						onClick={onOpen}
						className={`group/menu-tooltip relative flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${iconButton}`}
						aria-label="Expand navigation"
					>
						<span className="relative flex h-6 w-6 items-center justify-center">
							<img
								src={Logo}
								alt=""
								className={`h-6 w-6 transition-opacity duration-150 ${
									showCollapsedMenuIcon ? 'opacity-0' : 'opacity-100'
								}`}
							/>
							<Bars3Icon
								className={`absolute h-6 w-6 transition-opacity duration-150 ${
									showCollapsedMenuIcon ? 'opacity-100' : 'opacity-0'
								}`}
							/>
						</span>
						<Tooltip isLight={isLight}>Expand menu</Tooltip>
					</button>
				)}

				{isOpen && (
					<button
						type="button"
						onClick={onClose}
						className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${iconButton}`}
						aria-label="Collapse navigation"
						title="Collapse navigation"
					>
						<ChevronLeftIcon className="h-5 w-5" />
					</button>
				)}
			</div>

			<nav className={`flex-grow ${isOpen ? 'px-3 pt-4' : 'px-2 pt-4'}`}>
				<ul className="space-y-2">
					{navItems.map(item => {
						const isActive = isActivePath(item.path)

						return (
							<li key={item.name}>
								<Link
									to={item.path}
									className={`group/menu-tooltip relative flex h-11 items-center rounded-xl text-[15px] transition-colors ${
										isOpen ? 'px-3' : 'justify-center px-0'
									} ${isActive ? activeItem : inactiveItem}`}
									aria-label={!isOpen ? item.name : undefined}
								>
									<item.icon className={`h-5 w-5 shrink-0 ${isOpen ? 'mr-3' : ''}`} />
									{isOpen && (
										<span className="truncate">{item.name}</span>
									)}
									{!isOpen && (
										<Tooltip isLight={isLight}>{item.name}</Tooltip>
									)}
								</Link>
							</li>
						)
					})}
				</ul>
			</nav>

			<div className={`border-t ${isOpen ? 'px-3 pb-5 pt-3' : 'px-2 pb-5 pt-3'} ${isLight ? 'border-gray-200' : 'border-white/[0.06]'}`}>
				<button
					type="button"
					onClick={handleLogout}
					disabled={isLoggingOut}
					className={`group/menu-tooltip relative flex h-11 w-full items-center rounded-xl text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
						isOpen ? 'px-3' : 'justify-center px-0'
					} ${isLoggingOut ? subtleText : inactiveItem}`}
					aria-label={!isOpen ? 'Logout' : undefined}
				>
					{isLoggingOut ? (
						<svg className={`animate-spin h-[18px] w-[18px] shrink-0 ${isOpen ? 'mr-3' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
					) : (
						<ArrowRightStartOnRectangleIcon className={`h-[18px] w-[18px] shrink-0 ${isOpen ? 'mr-3' : ''}`} />
					)}
					{isOpen && (
						<span className="truncate">{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
					)}
					{!isOpen && (
						<Tooltip isLight={isLight}>{isLoggingOut ? 'Logging out...' : 'Logout'}</Tooltip>
					)}
				</button>
			</div>
		</aside>
	)
}

export default SidePanel
